<template>
  <div class="min-h-screen bg-white text-gray-dark lg:hidden">
    <div
      class="mx-auto flex min-h-screen w-full max-w-[640px] flex-col px-5 py-5 md:max-w-[760px] md:px-10 md:py-10"
    >
      <div class="flex min-h-full flex-1 flex-col gap-6 md:gap-8">
        <div class="flex h-7 items-center justify-between md:h-9">
          <NuxtLink to="/" class="flex items-center" aria-label="На главную">
            <img
              src="/login/logo-mark.webp"
              alt="АгроПромШина"
              class="h-8 w-8 object-contain md:h-10 md:w-10"
            />
          </NuxtLink>
        </div>

        <section class="w-full max-w-[560px] md:pt-6">
          <form
            class="flex flex-col gap-3 md:gap-4"
            @submit.prevent="submitLogin"
          >
            <div class="flex flex-col gap-1">
              <p
                class="text-[28px] leading-[1.05] font-helvetica-700 font-bold md:text-[40px]"
              >
                Вход
              </p>
              <p
                class="text-[14px] leading-[20px] font-helvetica-400 text-extra-text-color md:max-w-[420px] md:text-[16px] md:leading-[22px]"
              >
                Введите логин и пароль, чтобы получить доступ в систему.
              </p>
            </div>

            <label class="flex flex-col gap-2">
              <span
                class="text-[14px] leading-[18px] font-helvetica-400 md:text-[15px] md:leading-[20px]"
                :class="errors.login ? 'text-orange' : 'text-[#44454a]'"
              >
                Логин
              </span>
              <input
                v-model="form.login"
                type="text"
                name="login"
                autocomplete="username"
                placeholder="Введите логин"
                class="h-[46px] w-full rounded-[14px] border bg-white px-[14px] text-[16px] font-helvetica-400 transition-colors placeholder:text-extra-text-color/80 md:h-[52px] md:rounded-[16px] md:px-4 md:text-[17px]"
                :class="inputClass(errors.login)"
                @focus="isFocused = true"
                @blur="
                  () => {
                    touch('login');
                    isFocused = false;
                  }
                "
              />
              <p
                v-if="errors.login"
                class="text-[12px] leading-[16px] text-orange"
              >
                {{ errors.login }}
              </p>
            </label>

            <label class="flex flex-col gap-2">
              <span
                class="text-[14px] leading-[18px] font-helvetica-400 md:text-[15px] md:leading-[20px]"
                :class="errors.password ? 'text-orange' : 'text-[#44454a]'"
              >
                Пароль
              </span>

              <div class="relative flex items-center">
                <input
                  v-model="form.password"
                  :type="showPassword ? 'text' : 'password'"
                  name="password"
                  autocomplete="current-password"
                  placeholder="Введите пароль"
                  class="h-[46px] w-full rounded-[14px] border bg-white px-[14px] pr-11 text-[16px] font-helvetica-400 transition-colors placeholder:text-extra-text-color/80 md:h-[52px] md:rounded-[16px] md:px-4 md:pr-12 md:text-[17px]"
                  :class="inputClass(errors.password)"
                  @focus="isFocused = true"
                  @blur="
                    () => {
                      touch('password');
                      isFocused = false;
                    }
                  "
                />
                <button
                  type="button"
                  class="absolute right-3 flex h-6 w-6 items-center justify-center text-gray-light md:right-4"
                  @click="togglePassword"
                  aria-label="Показать или скрыть пароль"
                >
                  <SvgoIconComponentLIconEye alt="Показать пароль" class="h-6 w-6"></SvgoIconComponentLIconEye>
                </button>
              </div>
              <p
                v-if="errors.password"
                class="text-[12px] leading-[16px] text-orange"
              >
                {{ errors.password }}
              </p>
            </label>

            <label
              class="mt-1 flex items-center gap-[10px] cursor-pointer select-none md:gap-3"
            >
              <input v-model="form.remember" type="checkbox" class="hidden" />
              <span
                class="flex h-5 w-5 items-center justify-center rounded-[6px] border border-orange transition-colors md:h-5.5 md:w-5.5"
                :class="
                  form.remember
                    ? 'bg-orange text-white'
                    : 'bg-white text-transparent'
                "
              >
                <svg
                  v-if="form.remember"
                  width="12"
                  height="10"
                  viewBox="0 0 14 14"
                  fill="none"
                  xmlns="http://www.w3.org/2000/svg"
                >
                  <path
                    d="M2 7L5.25 10L12 3"
                    stroke="currentColor"
                    stroke-width="2"
                    stroke-linecap="round"
                    stroke-linejoin="round"
                  />
                </svg>
              </span>
              <span
                class="text-[14px] leading-[18px] font-helvetica-400 text-[#44454a] md:text-[15px] md:leading-[20px]"
              >
                Запомнить данные для входа
              </span>
            </label>

            <button
              type="submit"
              class="mt-1 h-[46px] rounded-[14px] border border-orange bg-orange text-[14px] font-helvetica-700 font-bold text-white transition-colors hover:bg-selected-orange disabled:cursor-not-allowed disabled:opacity-70 md:h-[52px] md:rounded-[16px] md:text-[16px]"
              :disabled="loading"
            >
              Войти
            </button>
            <button
              type="button"
              class="h-[46px] rounded-[14px] border border-gray-dark bg-white text-[14px] font-helvetica-700 font-bold text-gray-dark transition-colors hover:bg-gray-dark hover:text-white md:h-[52px] md:rounded-[16px] md:text-[16px]"
              @click="goToRegister"
            >
              Зарегистрироваться
            </button>

            <p
              class="pt-1 text-[14px] leading-[20px] font-helvetica-400 text-extra-text-color md:pt-2 md:text-[15px] md:leading-[22px]"
            >
              Для получения логина и пароля отправьте заявку на регистрацию, или
              обратитесь в отдел развития ООО “Агропромшина” по телефону +7
              (499) 403-39-06.
            </p>
            <p
              v-if="submitError"
              class="text-[13px] leading-[18px] font-helvetica-400 text-orange"
            >
              {{ submitError }}
            </p>
          </form>
        </section>

        <div
          class="mt-auto text-center text-[16px] font-helvetica-400 text-gray-dark md:pt-2 md:text-[17px]"
        >
          © ООО “Агропромшина”
        </div>
      </div>
    </div>
  </div>

  <div
    class="hidden min-h-screen overflow-hidden bg-white text-gray-dark lg:flex lg:flex-row"
  >
    <aside
      class="relative h-screen w-[min(48vw,680px)] min-w-[520px] overflow-hidden bg-white px-[clamp(44px,5vw,84px)] py-[clamp(32px,6vh,72px)]"
    >
      <img
        :src="bannerTexture"
        alt=""
        aria-hidden="true"
        class="pointer-events-none absolute inset-0 h-full w-full object-cover"
      />

      <div class="relative z-10 flex min-h-full w-full flex-col">
        <div class="flex w-full flex-1 items-center">
          <div class="flex w-full max-w-[560px] flex-col gap-7 text-gray-dark">
            <div
              class="flex w-[480px] max-w-full flex-col justify-center rounded-[20px] bg-[#FFFFFE] px-10 py-10"
            >
              <form class="flex flex-col gap-5" @submit.prevent="submitLogin">
                <div class="flex flex-col gap-6">
                  <label class="flex flex-col gap-2">
                    <span
                      class="font-helvetica text-[16px] leading-[24px]"
                      :class="errors.login ? 'text-orange' : 'text-gray-dark'"
                    >
                      Логин
                    </span>
                    <input
                      v-model="form.login"
                      type="text"
                      name="login"
                      autocomplete="username"
                      placeholder="Введите логин"
                      class="h-[48px] w-full rounded-[10px] border px-4 text-[16px] font-helvetica transition-colors"
                      :class="inputClass(errors.login)"
                      @focus="isFocused = true"
                      @blur="
                        () => {
                          touch('login');
                          isFocused = false;
                        }
                      "
                    />
                    <p
                      v-if="errors.login"
                      class="text-orange text-[12px] leading-[16px]"
                    >
                      {{ errors.login }}
                    </p>
                  </label>

                  <label class="flex flex-col gap-2">
                    <div class="flex items-center justify-between">
                      <span
                        class="font-helvetica text-[16px] leading-[24px]"
                        :class="
                          errors.password ? 'text-orange' : 'text-gray-dark'
                        "
                      >
                        Пароль
                      </span>
                    </div>

                    <div class="relative flex w-full items-center">
                      <input
                        v-model="form.password"
                        :type="showPassword ? 'text' : 'password'"
                        name="password"
                        autocomplete="current-password"
                        placeholder="Введите пароль"
                        class="h-[48px] w-full rounded-[10px] border bg-transparent px-4 pr-10 text-[16px] font-helvetica outline-none transition-colors"
                        :class="inputClass(errors.password)"
                        @focus="isFocused = true"
                        @blur="
                          () => {
                            touch('password');
                            isFocused = false;
                          }
                        "
                      />
                      <button
                        type="button"
                        class="absolute right-3 shrink-0 cursor-pointer"
                        @click="togglePassword"
                        aria-label="Показать или скрыть пароль"
                      >
                        <SvgoIconComponentLIconEye alt="Показать пароль" class="h-6 w-6 text-gray-light hover:text-gray-dark"></SvgoIconComponentLIconEye>
                      </button>
                    </div>
                    <p
                      v-if="errors.password"
                      class="text-orange text-[12px] leading-[16px]"
                    >
                      {{ errors.password }}
                    </p>
                  </label>
                </div>

                <label
                  class="flex cursor-pointer items-center gap-3 select-none text-[16px] font-helvetica"
                >
                  <input
                    v-model="form.remember"
                    type="checkbox"
                    class="hidden"
                  />
                  <span
                    class="flex h-5 w-5 items-center justify-center rounded-[2px] border-2 transition-colors"
                    :class="
                      form.remember
                        ? 'border-orange bg-orange'
                        : 'border-orange bg-white'
                    "
                  >
                    <svg
                      v-if="form.remember"
                      width="14"
                      height="14"
                      viewBox="0 0 14 14"
                      fill="none"
                      class="text-white-card"
                      xmlns="http://www.w3.org/2000/svg"
                    >
                      <path
                        d="M2 7L5.25 10L12 3"
                        stroke="currentColor"
                        stroke-width="2"
                        stroke-linecap="round"
                        stroke-linejoin="round"
                      />
                    </svg>
                  </span>
                  <span>Запомнить данные для входа</span>
                </label>

                <button
                  type="submit"
                  class="h-[48px] cursor-pointer rounded-[10px] border border-orange bg-orange text-[16px] font-bold text-white transition-all hover:bg-transparent hover:text-orange disabled:cursor-not-allowed disabled:opacity-70"
                  :disabled="loading"
                >
                  Войти
                </button>
                <button
                  type="button"
                  class="h-[48px] cursor-pointer rounded-[10px] border border-gray-dark bg-white text-[16px] text-gray-dark transition-all hover:bg-gray-dark hover:text-white"
                  @click="goToRegister"
                >
                  Зарегистрироваться
                </button>

                <p
                  class="text-center text-[16px] leading-[24px] text-gray-light font-helvetica"
                >
                  Для получения логина и пароля отправьте заявку на регистрацию,
                  или обратитесь в отдел развития ООО “Агропромшина” по телефону
                  +7 (499) 403-39-06
                </p>
                <p
                  v-if="submitError"
                  class="text-center text-[14px] leading-[20px] text-orange font-helvetica"
                >
                  {{ submitError }}
                </p>
              </form>
            </div>
          </div>
        </div>

        <p class="mt-10 text-[16px] font-helvetica text-extra-text-color">
          © ООО “Агропромшина”
        </p>
      </div>
    </aside>

    <div class="hidden h-screen flex-1 overflow-hidden lg:flex">
      <img
        :src="authImage"
        alt="Промо баннер АгроПромШина"
        class="h-full w-full object-cover"
      />
    </div>
  </div>
</template>

<script setup lang="ts">
import { onMounted, reactive, ref, watch } from "vue";
import { useRouter, useNuxtApp, useRoute } from "#imports";
import authImage from "~/assets/webp/image_2026-02-10_16-34-35-2.webp";
import bannerTexture from "~/assets/webp/banner.webp";
import { useAuthBootstrap } from "~/composables/useAuthBootstrap";
import { useHomeNews } from "~/composables/useHomeNews";
import { useLoginResponse } from "~/composables/useLoginResponse";

definePageMeta({
  layout: false,
});

usePageTitle("Вход");

const router = useRouter();
const route = useRoute();
const nuxtApp = useNuxtApp();
const $useOneCApi = nuxtApp.$useOneCApi as (
  action: string,
  payload?: Record<string, any>,
) => Promise<any>;
const { markAuthorized, consumeReturnTo, persistGuidFromLoginResponse } =
  useAuthBootstrap();
const { loadNews } = useHomeNews();
const { persistLoginResponse } = useLoginResponse();
const form = reactive({
  login: "",
  password: "",
  remember: false,
});

const touched = reactive<{ login: boolean; password: boolean }>({
  login: false,
  password: false,
});

const errors = reactive<{ login: string; password: string }>({
  login: "",
  password: "",
});

const showPassword = ref(false);
const isFocused = ref(false);
const loading = ref(false);
const submitError = ref("");
const LS_KEY = "login_credentials";
const SESSION_POST_LOGIN_BANNERS_PENDING_KEY = "apsh_post_login_news_pending";

onMounted(() => {
  if (process.client) {
    try {
      const raw = localStorage.getItem(LS_KEY);
      if (raw) {
        const saved = JSON.parse(raw);
        form.login = saved.login || "";
        form.password = saved.password || "";
        form.remember = !!saved.remember;
      }
    } catch (e) {
      console.error("Failed to read saved credentials", e);
    }
  }
});

watch(
  () => ({ ...form }),
  (val) => {
    if (!process.client) return;
    if (val.remember && val.login && val.password) {
      localStorage.setItem(
        LS_KEY,
        JSON.stringify({
          login: val.login,
          password: val.password,
          remember: true,
        }),
      );
    } else {
      localStorage.removeItem(LS_KEY);
    }
  },
  { deep: true },
);

const togglePassword = () => {
  showPassword.value = !showPassword.value;
};

const touch = (field: "login" | "password") => {
  touched[field] = true;
  validateField(field);
};

const validateField = (field: "login" | "password") => {
  if (field === "login") {
    if (!form.login.trim()) {
      errors.login = "Введите логин";
    } else if (form.login.trim().length < 3) {
      errors.login = "Логин должен быть длиннее";
    } else {
      errors.login = "";
    }
  }

  if (field === "password") {
    if (!form.password.trim()) {
      errors.password = "Введите пароль";
    } else if (form.password.length < 6) {
      errors.password = "Минимум 6 символов";
    } else {
      errors.password = "";
    }
  }
};

const validateAll = () => {
  validateField("login");
  validateField("password");
  return !errors.login && !errors.password;
};

const submitLogin = () => {
  touch("login");
  touch("password");

  if (validateAll()) {
    submitError.value = "";
    loading.value = true;

    $useOneCApi("login", {
      username: form.login,
      password: form.password,
    })
      .then(async (res: any) => {
        if (process.client) {
          try {
            persistLoginResponse(res);
            sessionStorage.setItem(SESSION_POST_LOGIN_BANNERS_PENDING_KEY, "1");
          } catch (e) {
            console.error("Failed to save login response", e);
          }
        }

        await loadNews();
        persistGuidFromLoginResponse(res);
        markAuthorized();
        const redirectPath = consumeReturnTo(route.query.redirect);
        router.push(redirectPath);
      })
      .catch((err: any) => {
        submitError.value =
          err?.data?.message ||
          err?.message ||
          "Не удалось выполнить вход. Проверьте данные и попробуйте снова.";
      })
      .finally(() => {
        loading.value = false;
      });
  }
};

const goToRegister = () => {
  router.push("/register");
};

const inputClass = (error: string) =>
  [
    error ? "border-orange" : "border-gray-border lg:border-gray-dark",
    "focus:border-orange",
    "focus:outline-none",
  ].join(" ");
</script>
